import { menuRows, resetSettings, saveSettings, settings, type Toggle } from '../settings';
import type { SettingDef, SettingGroup, SettingMenu, Settings } from '../settingsRegistry';
import { closeButton, h } from './dom';
import './settingsConfirm.css';

export interface SettingsHooks {
  /** Applies volume and mute to the audio. */
  applyAudio(): void;
  zoom(step: number): void;
  openHelp(): void;
  save(): void;
  /** Saves, then back to the character select (still logged in). */
  quitToTitle(): void;
  /** Saves, then logs out: back to the login screen. */
  logout(): void;
}

/** Settings menu (Esc): sound, visuals, camera, and game actions. Saved per browser. */
export class SettingsMenu {
  private readonly root: HTMLElement;
  private readonly boxes = new Map<Toggle, HTMLInputElement>();
  /** Drop-downs (number settings with `menu.choices`), by key. */
  private readonly picks = new Map<keyof Settings, HTMLSelectElement>();
  /** Lines beside rows (`menu.note`), worked out again after every change. */
  private readonly notes: [SettingDef, HTMLElement][] = [];
  private readonly vol: HTMLInputElement;
  /** The yes/no box over the card for leaving the world (log out, character select). */
  private readonly confirmBox = h('div', { class: 'set-confirm', hidden: true, role: 'alertdialog' });

  constructor(hooks: SettingsHooks) {
    const btn = (label: string, fn: () => void, cls = 'btn small ghost') => {
      const b = h('button', { type: 'button', class: cls }, label);
      b.addEventListener('click', fn);
      return b;
    };
    const change = () => {
      saveSettings();
      hooks.applyAudio();
      this.syncNotes();
    };
    this.vol = h('input', {
      type: 'range',
      min: '0',
      max: '100',
      value: String(Math.round(settings.volume * 100)),
    });
    const volText = h('b', {}, `${Math.round(settings.volume * 100)}%`);
    this.vol.addEventListener('input', () => {
      settings.volume = Number(this.vol.value) / 100;
      volText.textContent = `${this.vol.value}%`;
      change();
    });
    const mus = h('input', {
      type: 'range',
      min: '0',
      max: '100',
      value: String(Math.round(settings.music * 100)),
    });
    const musText = h('b', {}, `${Math.round(settings.music * 100)}%`);
    mus.addEventListener('input', () => {
      settings.music = Number(mus.value) / 100;
      musText.textContent = `${mus.value}%`;
      change();
    });
    // The checkboxes and drop-downs: every setting declared with a place in the menu (settingsRegistry.ts).
    const rows = (group: SettingGroup) =>
      menuRows(group).map((d) => {
        const menu = d.menu!;
        const note = menu.note ? h('span', { class: 'set-note', hidden: true }) : null;
        if (note) this.notes.push([d, note]);
        const values = settings as unknown as Record<string, boolean | number>;
        const action = menu.action ? this.actionButton(menu.action) : null;
        const row = action ? 'set-row set-act' : 'set-row';
        if (menu.choices) {
          const pick = h('select', {}, ...menu.choices.map((c, i) => h('option', { value: String(i) }, c)));
          pick.value = String(values[d.key]);
          pick.addEventListener('change', () => {
            values[d.key] = Number(pick.value);
            (menu.changed as ((v: number) => void) | undefined)?.(Number(pick.value));
            change();
            this.syncInputs();
          });
          this.picks.set(d.key, pick);
          return h('label', { class: row }, menu.label, pick, note, action);
        }
        const k = d.key as Toggle;
        const box = h('input', { type: 'checkbox', checked: settings[k] });
        box.addEventListener('change', () => {
          settings[k] = box.checked;
          (menu.changed as ((v: boolean) => void) | undefined)?.(box.checked);
          change();
        });
        this.boxes.set(k, box);
        return h('label', { class: row }, box, menu.label, note, action);
      });
    const dataRows = rows('data');
    this.root = h(
      'div',
      { id: 'settings', hidden: true, role: 'dialog', ariaLabel: 'ตั้งค่า', ariaModal: 'true' },
      h(
        'div',
        { class: 'set-card' },
        h(
          'header',
          {},
          h('h2', {}, 'ตั้งค่า'),
          h('kbd', {}, 'Esc'),
          closeButton(() => this.close()),
        ),
        h(
          'section',
          {},
          h('h4', {}, 'เสียง'),
          h('label', { class: 'set-row' }, 'ระดับเสียง', this.vol, volText),
          h('label', { class: 'set-row' }, 'เพลงประกอบ', mus, musText),
          ...rows('sound'),
        ),
        h('section', {}, h('h4', {}, 'ภาพและเอฟเฟกต์'), ...rows('visual')),
        dataRows.length ? h('section', {}, h('h4', {}, 'ข้อมูลในเครื่อง'), ...dataRows) : null,
        h(
          'section',
          {},
          h('h4', {}, 'กล้อง'),
          h(
            'div',
            { class: 'set-btns' },
            btn('ซูมเข้า', () => hooks.zoom(1)),
            btn('ซูมออก', () => hooks.zoom(-1)),
          ),
        ),
        h(
          'section',
          {},
          h('h4', {}, 'เกม'),
          h(
            'div',
            { class: 'set-btns' },
            btn('วิธีเล่นและปุ่มควบคุม', () => {
              this.close();
              hooks.openHelp();
            }),
            btn('บันทึกเกม', () => hooks.save()),
            btn('คืนค่าตั้งค่าเริ่มต้น', () => {
              resetSettings();
              this.syncInputs();
              hooks.applyAudio();
            }),
          ),
          h(
            'div',
            { class: 'set-btns set-leave' },
            btn('กลับไปเลือกตัวละคร', () =>
              this.ask('กลับไปหน้าเลือกตัวละคร?', 'เกมจะบันทึกความคืบหน้าก่อนออกจากโลก', () => hooks.quitToTitle()),
            ),
            btn('ออกจากระบบ', () =>
              this.ask('ออกจากระบบ?', 'เกมจะบันทึกความคืบหน้า แล้วกลับไปหน้าเข้าสู่ระบบ', () => hooks.logout()),
            ),
          ),
        ),
        this.confirmBox,
      ),
    );
    this.root.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      if (e.target === this.root) this.close();
    });
    document.body.append(this.root);
  }

  get open(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
    if (!this.root.hidden) this.syncInputs();
  }

  close(): void {
    this.root.hidden = true;
    this.confirmBox.hidden = true;
  }

  /** Asks yes/no in the card; `yes` runs once, on the confirm button. */
  private ask(title: string, detail: string, yes: () => void): void {
    const ok = h('button', { type: 'button', class: 'btn small' }, 'ยืนยัน');
    const no = h('button', { type: 'button', class: 'btn small ghost' }, 'ยกเลิก');
    ok.addEventListener('click', () => {
      ok.disabled = no.disabled = true;
      ok.textContent = 'กำลังบันทึก…';
      yes();
    });
    no.addEventListener('click', () => (this.confirmBox.hidden = true));
    this.confirmBox.replaceChildren(
      h('div', { class: 'set-confirm-card' }, h('h3', {}, title), h('p', {}, detail), h('div', { class: 'set-btns' }, no, ok)),
    );
    this.confirmBox.hidden = false;
    setTimeout(() => no.focus(), 0);
  }

  /** A row's button (`menu.action`): busy while it runs, then the notes are worked out again. */
  private actionButton(action: NonNullable<SettingMenu['action']>): HTMLButtonElement {
    const b = h('button', { type: 'button', class: 'btn small ghost' }, action.label);
    b.addEventListener('click', (e) => {
      // Inside the row's label: a click must not also reach the row's own input.
      e.preventDefault();
      b.disabled = true;
      void Promise.resolve()
        .then(action.run)
        .catch((error: unknown) => console.warn(`Setting "${action.label}" failed`, error))
        .finally(() => {
          b.disabled = false;
          this.syncNotes();
        });
    });
    return b;
  }

  private syncInputs(): void {
    this.vol.value = String(Math.round(settings.volume * 100));
    for (const [k, box] of this.boxes) box.checked = settings[k];
    for (const [k, pick] of this.picks) pick.value = String(settings[k]);
    this.syncNotes();
  }

  private syncNotes(): void {
    for (const [d, el] of this.notes) {
      const text = d.menu?.note?.() ?? '';
      el.textContent = text;
      el.hidden = !text;
    }
  }
}
